import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import { IPD_ORDERS_CHANGED_EVENT, IPD_TASKS_CHANGED_EVENT, type IpdTaskView, type IpdTaskWindow } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { cn, dateTime, display, timeOnly } from '@/lib/utils';
import { Alert, Button, Skeleton } from '@/components/ui/primitives';
import { ShellEmpty, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, type Admission } from '../../admission/types';
import { RecordTaskDialog } from '../../tasks/RecordTaskDialog';
import { TaskDetailsDialog } from '../../tasks/TaskDetailsDialog';
import { OrderBadges, TaskStatusChip } from '../../tasks/TaskChip';
import { CHART_HOURS, HOUR_MS, chartColumns, chartRows, chartStart, nowMarker, shiftChart, type ChartColumn } from '../../tasks/task-chart';
import { CHART_LEGEND, orderKindLabel, taskStatusLabel, taskTitle } from '../../tasks/task-labels';
import { displayStatus, taskIsPending } from '../../tasks/record-task';
import { admissionTasksKey, mayRecordTask, storeRecordedTask, useTaskClock } from '../../tasks/task-queries';
import { orderKindIcon } from '../../tasks/order-kind-icon';

const CHART_EVENTS = [IPD_TASKS_CHANGED_EVENT, IPD_ORDERS_CHANGED_EVENT, 'cpoe.ordered'];
const SKELETON_ROWS = ['r1', 'r2', 'r3'];

function hourText(at: number, timeZone: string): string {
  return new Intl.DateTimeFormat(display.locale, { hour: 'numeric', timeZone }).format(at);
}

function dayText(at: number, timeZone: string): string {
  return new Intl.DateTimeFormat(display.locale, { day: 'numeric', month: 'short', timeZone }).format(at);
}

export function TaskChartSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const timeZone = display.timezone;
  const current = admissionIsOpen(admission);
  const [start, setStart] = useState(() => chartStart(Date.now(), timeZone));
  const [recording, setRecording] = useState<IpdTaskView | null>(null);
  const [viewing, setViewing] = useState<IpdTaskView | null>(null);
  const nowCell = useRef<HTMLTableCellElement | null>(null);
  const from = new Date(start).toISOString();
  const to = new Date(start + CHART_HOURS * HOUR_MS).toISOString();

  useRealtimeInvalidate([`admission:${admission.id}`, `emar:${admission.id}`], CHART_EVENTS, [admissionTasksKey(admission.id)]);
  const tasks = useQuery({
    queryKey: admissionTasksKey(admission.id, from),
    queryFn: () => get<IpdTaskWindow>(`/ipd/admissions/${admission.id}/tasks${qs({ from, to })}`),
  });
  const now = useTaskClock(tasks.data?.serverTime, tasks.dataUpdatedAt);
  const columns = useMemo(() => chartColumns(start, timeZone), [start, timeZone]);
  const rows = useMemo(() => chartRows(tasks.data?.data ?? [], start), [tasks.data, start]);
  const marker = nowMarker(start, now);
  const atNow = start === chartStart(now, timeZone);
  const loaded = tasks.isSuccess;

  useEffect(() => {
    if (loaded) nowCell.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [loaded, start]);

  const open = (task: IpdTaskView) => {
    if (taskIsPending(task) && current && mayRecordTask(can, task)) setRecording(task);
    else setViewing(task);
  };

  return (
    <>
      <ShellSectionHeading
        icon={ListChecks}
        title={tr('ipd', 'io_task_chart_title')}
        description={tr('ipd', 'io_task_chart_desc')}
        actions={
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" aria-label={tr('ipd', 'io_task_previous_day')} onClick={() => setStart((value) => shiftChart(value, -1))}>
              <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
            </Button>
            <Button size="sm" variant="outline" disabled={atNow} onClick={() => setStart(chartStart(Date.now(), timeZone))}>
              {tr('ipd', 'io_task_now')}
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8" aria-label={tr('ipd', 'io_task_next_day')} onClick={() => setStart((value) => shiftChart(value, 1))}>
              <ChevronRight className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
            </Button>
          </div>
        }
      />

      <p className="mt-2 px-1 text-xs text-muted-foreground tabular">{tr('ipd', 'io_task_chart_range', { from: dateTime(from), to: dateTime(to) })}</p>

      {tasks.isError && (
        <Alert variant="destructive" className="mt-3">
          {describeError(tasks.error)}
        </Alert>
      )}

      <div className="mt-3 overflow-x-auto rounded-xl border bg-card" aria-busy={tasks.isFetching || undefined}>
        <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
          <caption className="sr-only">{tr('ipd', 'io_task_chart_title')}</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky start-0 z-20 w-36 min-w-36 border-b border-e bg-card px-2 py-1.5 text-start text-xs font-medium text-muted-foreground sm:w-52 sm:min-w-52">
                {tr('ipd', 'io_task_order')}
              </th>
              {columns.map((column) => (
                <HourHeader key={column.index} column={column} timeZone={timeZone} marker={marker?.column === column.index ? marker.fraction : null} cellRef={marker?.column === column.index ? nowCell : undefined} />
              ))}
            </tr>
          </thead>
          <tbody>
            {tasks.isLoading &&
              SKELETON_ROWS.map((key) => (
                <tr key={key}>
                  <td className="sticky start-0 z-10 border-b border-e bg-card p-2">
                    <Skeleton className="h-4 w-28" />
                  </td>
                  <td colSpan={CHART_HOURS} className="border-b p-2">
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))}
            {rows.map((row) => {
              const Icon = orderKindIcon(row.order.kind);
              return (
                <tr key={row.orderId}>
                  <th scope="row" className="sticky start-0 z-10 w-36 min-w-36 max-w-36 border-b border-e bg-card px-2 py-1.5 text-start align-top font-normal sm:w-52 sm:min-w-52 sm:max-w-52">
                    <div className="flex items-start gap-1.5">
                      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      <div className="min-w-0">
                        <p className="truncate font-medium" title={row.order.label}>
                          {row.order.label}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{orderKindLabel(row.order.kind)}</p>
                        <OrderBadges priority={row.order.priority} isHighAlert={row.order.isHighAlert} state={row.order.state} className="mt-1" />
                      </div>
                    </div>
                  </th>
                  {row.cells.map((cell, index) => (
                    <td key={columns[index]?.start ?? index} className={cn('relative min-w-[5.25rem] border-b px-1 py-1.5 align-top', columns[index]?.startsDay && index > 0 && 'border-s')}>
                      {marker?.column === index && <NowLine fraction={marker.fraction} />}
                      <div className="relative flex flex-col items-start gap-1">
                        {cell.map((task) => (
                          <TaskCellChip key={task.id} task={task} now={now} onOpen={open} />
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {loaded && rows.length === 0 && (
        <div className="mt-3">
          <ShellEmpty>{tr('ipd', 'io_task_chart_empty')}</ShellEmpty>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5 px-1" aria-label={tr('ipd', 'legend')}>
        <span className="me-1 text-xs text-muted-foreground">{tr('ipd', 'legend')}</span>
        {CHART_LEGEND.map((status) => (
          <TaskStatusChip key={status} status={status} />
        ))}
        <span className="ms-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <span className="h-3 w-0.5 rounded-full bg-primary" aria-hidden />
          {tr('ipd', 'io_task_now')}
        </span>
      </div>

      {recording !== null && (
        <RecordTaskDialog
          task={recording}
          onClose={() => setRecording(null)}
          onSaved={(saved) => {
            setRecording(null);
            storeRecordedTask(queryClient, saved, admission.bed?.room.ward.id ?? null);
          }}
        />
      )}
      {viewing !== null && <TaskDetailsDialog task={viewing} now={now} onClose={() => setViewing(null)} />}
    </>
  );
}

function HourHeader({
  column,
  timeZone,
  marker,
  cellRef,
}: {
  column: ChartColumn;
  timeZone: string;
  marker: number | null;
  cellRef?: Ref<HTMLTableCellElement>;
}) {
  return (
    <th
      ref={cellRef}
      scope="col"
      className={cn('relative min-w-[5.25rem] border-b px-1 py-1 text-start align-bottom text-xs font-medium tabular', column.startsDay && column.index > 0 && 'border-s', marker !== null ? 'text-primary' : 'text-muted-foreground')}
    >
      {marker !== null && <NowLine fraction={marker} />}
      <span className="block h-4 truncate text-[10px] font-normal">{column.startsDay ? dayText(column.start, timeZone) : ''}</span>
      <span className="block">{hourText(column.start, timeZone)}</span>
    </th>
  );
}

function NowLine({ fraction }: { fraction: number }) {
  return <span className="pointer-events-none absolute inset-y-0 w-0.5 bg-primary/70" style={{ insetInlineStart: `${Math.round(fraction * 1000) / 10}%` }} aria-hidden />;
}

function TaskCellChip({ task, now, onOpen }: { task: IpdTaskView; now: number; onOpen: (task: IpdTaskView) => void }) {
  const status = displayStatus(task, now);
  const time = timeOnly(task.scheduledAt);
  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      className="focus-ring rounded-full"
      aria-label={tr('ipd', 'io_task_chip_label', { task: taskTitle(task), time, status: taskStatusLabel(status) })}
      title={`${taskTitle(task)} · ${taskStatusLabel(status)}`}
    >
      <TaskStatusChip status={status} label={time} className="gap-1 px-1.5 text-[11px] tabular" />
    </button>
  );
}
